import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatPrice, categoryLabels } from '../utils/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import './ProductDetail.css';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`)
      .then(r => setProduct(r.data.product))
      .catch(() => navigate('/shop'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleAddToCart = () => {
    addToCart(product, quantity);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please login to review'); return; }
    setSubmitting(true);
    try {
      await api.post(`/products/${product._id}/reviews`, reviewForm);
      toast.success('Review submitted!');
      const { data } = await api.get(`/products/${id}`);
      setProduct(data.product);
      setReviewForm({ rating: 5, comment: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading-center" style={{ minHeight: '60vh' }}><div className="spinner"></div></div>;
  if (!product) return null;

  const price = product.discountPrice || product.price;
  const hasDiscount = product.discountPrice && product.discountPrice < product.price;

  return (
    <div className="product-detail container">
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <a href="/">Home</a> › <a href="/shop">Shop</a> › <a href={`/shop?category=${product.category}`}>{categoryLabels[product.category]}</a> › {product.name}
      </nav>

      {/* Main layout */}
      <div className="detail-grid">
        {/* Images */}
        <div className="detail-images">
          <div className="main-image-wrap">
            <img
              src={product.images?.[activeImage]?.url || 'https://via.placeholder.com/500'}
              alt={product.name}
              className="main-image"
            />
            {hasDiscount && <span className="discount-badge">-{product.discountPercent}% OFF</span>}
          </div>
          {product.images?.length > 1 && (
            <div className="image-thumbs">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  className={`thumb ${activeImage === i ? 'active' : ''}`}
                  onClick={() => setActiveImage(i)}
                >
                  <img src={img.url} alt={img.alt || product.name} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="detail-info">
          <span className="badge badge-green">{categoryLabels[product.category]}</span>
          <h1 className="detail-name">{product.name}</h1>

          {/* Rating */}
          {product.numReviews > 0 && (
            <div className="detail-rating">
              {[1,2,3,4,5].map(s => (
                <span key={s} className={s <= Math.round(product.rating) ? 'star star-filled' : 'star star-empty'}>★</span>
              ))}
              <span className="review-count-link">({product.numReviews} reviews)</span>
            </div>
          )}

          {/* Price */}
          <div className="detail-price">
            <span className="price-big">{formatPrice(price)}</span>
            {hasDiscount && (
              <>
                <span className="price-crossed">{formatPrice(product.price)}</span>
                <span className="badge badge-sale">Save {formatPrice(product.price - price)}</span>
              </>
            )}
          </div>

          <p className="detail-desc">{product.description}</p>

          {/* Plant attributes */}
          <div className="plant-attrs">
            {product.careLevel && (
              <div className="attr-item">
                <span className="attr-icon">🎯</span>
                <div><small>Care Level</small><strong>{product.careLevel}</strong></div>
              </div>
            )}
            {product.sunlight && product.sunlight !== 'any' && (
              <div className="attr-item">
                <span className="attr-icon">☀️</span>
                <div><small>Sunlight</small><strong>{product.sunlight.replace('-', ' ')}</strong></div>
              </div>
            )}
            {product.wateringFrequency && (
              <div className="attr-item">
                <span className="attr-icon">💧</span>
                <div><small>Watering</small><strong>{product.wateringFrequency.replace('-', ' ')}</strong></div>
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="detail-tags">
            {product.petFriendly && <span className="badge badge-green">🐾 Pet Friendly</span>}
            {product.airPurifying && <span className="badge badge-green">💨 Air Purifying</span>}
          </div>

          {/* Stock */}
          <p className={`stock-status ${product.stock > 0 ? 'in-stock' : 'out-of-stock'}`}>
            {product.stock > 0 ? `✓ In Stock (${product.stock} available)` : '✗ Out of Stock'}
          </p>

          {/* Quantity + Add to cart */}
          {product.stock > 0 && (
            <div className="add-to-cart-row">
              <div className="qty-control">
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
                <span>{quantity}</span>
                <button onClick={() => setQuantity(q => Math.min(product.stock, q + 1))}>+</button>
              </div>
              <button className="btn btn-primary btn-lg" style={{ flex: 1 }} onClick={handleAddToCart}>
                🛒 Add to Cart
              </button>
            </div>
          )}

          <button className="btn btn-secondary" onClick={() => { addToCart(product, quantity); navigate('/cart'); }}>
            Buy Now →
          </button>
        </div>
      </div>

      {/* Reviews */}
      <section className="reviews-section">
        <h2 className="reviews-title">Customer Reviews ({product.numReviews})</h2>

        {/* Review list */}
        {product.reviews?.length > 0 ? (
          <div className="reviews-list">
            {product.reviews.map(r => (
              <div key={r._id} className="review-card card">
                <div className="review-header">
                  <div className="review-avatar">{r.name[0]}</div>
                  <div>
                    <strong>{r.name}</strong>
                    <div className="stars">
                      {[1,2,3,4,5].map(s => (
                        <span key={s} className={s <= r.rating ? 'star star-filled' : 'star star-empty'}>★</span>
                      ))}
                    </div>
                  </div>
                  <span className="review-date">{new Date(r.createdAt).toLocaleDateString()}</span>
                </div>
                <p>{r.comment}</p>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--gray-400)', marginBottom: 24 }}>No reviews yet. Be the first!</p>
        )}

        {/* Write review form */}
        {user && (
          <div className="review-form card">
            <h3>Write a Review</h3>
            <form onSubmit={handleReviewSubmit}>
              <div className="form-group">
                <label className="form-label">Your Rating</label>
                <div className="rating-stars">
                  {[1,2,3,4,5].map(s => (
                    <button
                      key={s}
                      type="button"
                      className={`rating-star ${reviewForm.rating >= s ? 'active' : ''}`}
                      onClick={() => setReviewForm(f => ({ ...f, rating: s }))}
                    >★</button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Your Review</label>
                <textarea
                  value={reviewForm.comment}
                  onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                  className="form-input"
                  rows={4}
                  placeholder="Share your experience with this plant..."
                  required
                />
              </div>
              <button className="btn btn-primary" type="submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          </div>
        )}
      </section>
    </div>
  );
}
